import React, { useEffect, useState } from 'react';
import ReactSelect from "react-select"
import { TextField, FormControl, Grid, Card, Typography, Box, Stack, IconButton, FormHelperText } from '@mui/material';
import { MobileTimePicker } from '@mui/x-date-pickers/MobileTimePicker';
import { useDispatch } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
// icons
import DeleteIcon from "common/customIcons/delete";
import AngleRightIcon from "common/customIcons/angle_right";
import ArrowDown from "common/customIcons/arrowDown";
// utils and helpers
import { grey } from "@mui/material/colors";
import { validateRegionData } from "validations/hyperwolf/regions/region";
import { showToastMsg } from "components/distributionSetting/distributionManagement/addRegionKit";
import { addRegion, getRegionDetails, getUnassignedDrivers, getUnassignedSubRegion, updateRegion } from "../../../../redux/slices/hyperwolf/settings";
import { openCustomModalDialog } from "../../../../redux/slices/modalSlice";
// custom components
import CommonLayout from "layouts/DashboardLayout";
import MDButton from 'components/MDButton';

export default function AddHyperwolfRegion() {
  const { regionId } = useParams()
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const [errors, setErrors] = useState({});
  const [details, setDetails] = useState({ status: true, subRegions: [{ subRegionId: "", driverId: "" }] });
  const [isLoading, setIsLoading] = useState(false);
  const [subRegions, setSubRegions] = useState([]);
  const [drivers, setDrivers] = useState([]);

  const handleRegionDetails = async () => {
    try {
      const response = await dispatch(getRegionDetails(regionId)).unwrap()
      if (response?.data) {
        const { closeTime, openTime, regionId, region, status, subRegions, } = response?.data || {}
        setDetails({
          closeTime: dayjs(closeTime, ['HH:mm', 'h:mm A', 'HH:mm:ss']),
          openTime: dayjs(openTime, ['HH:mm', 'h:mm A', 'HH:mm:ss']),
          regionId, region, status, subRegions,
        })
      }
    } catch (error) {
      showToastMsg(error, "error")
    }
  }

  const handleStatusChange = (tab) => {
    setDetails(prev => ({ ...prev, status: tab }))
  }

  const handleSubRegionsList = async () => {
    try {
      const response = await dispatch(getUnassignedSubRegion({ limit: 100, regionId: regionId || "" })).unwrap();
      if (response?.data) {
        setSubRegions(response?.data || [])
      }
    }
    catch (error) {
      showToastMsg(error, "error")
    }
  }

  const handleDriversList = async (search = "") => {
    try {
      const response = await dispatch(getUnassignedDrivers({ limit: 100, search, regionId: regionId || "" })).unwrap();
      if (response?.data) {
        setDrivers(response?.data || [])
      }
    }
    catch (error) {
      showToastMsg(error, "error")
    }
  }

  const handleSubRegionChange = (e, i) => {
    const { name, value } = e.target;

    setDetails(prev => {
      const newSubRegions = [...prev.subRegions];
      newSubRegions[i][name] = value;
      return { ...prev, subRegions: newSubRegions }
    })
  }

  const onDriverSelect = (driver, i) => {
    setDetails(prev => {
      const newSubRegions = [...prev.subRegions];
      newSubRegions[i].driverId = driver;
      return { ...prev, subRegions: newSubRegions }
    })
  }

  const handleSave = async () => {
    try {
      if (isLoading) return;
      setIsLoading(true);

      const { isValid, myErrors } = validateRegionData(details);
      if (!isValid) {
        setErrors(myErrors);
        return;
      } else {
        setErrors({});
      }

      let response = {};
      const request = {
        ...details,
        // openTime: dayjs(details.openTime).format("HH:mm"),
        // closeTime: dayjs(details.closeTime).format("HH:mm"),
      }

      if (regionId) {
        response = await dispatch(updateRegion(request)).unwrap();
      } else {
        response = await dispatch(addRegion(request)).unwrap();
      }
      if (response?._id || response?.message) {
        showToastMsg(response?.message || "Success", "success")
        navigate("/regions");
      }
    } catch (error) {
      showToastMsg(error, "error")
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    handleSubRegionsList();
    handleDriversList();
  }, [])

  useEffect(() => {
    if (regionId) {
      handleRegionDetails()
    }
  }, [regionId]);

  return (
    <CommonLayout>
      <Box p={{ xs: 2, sm: 3, lg: 4 }}>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          alignItems={{ sm: "center" }}
          justifyContent="space-between"
          gap={2}
          mb={2}
        >
          <Box display="flex" alignItems="center" color="text.main" lineHeight={1}>
            <IconButton onClick={() => navigate(-1)}>
              <AngleRightIcon fontSize="14px" />
            </IconButton>
            <Typography variant="body2" fontWeight="semibold" ml={0.75}>
              Regions
            </Typography>
          </Box>
        </Stack>
        <Grid spacing={4} container>
          <Grid item xs={12} lg={12}>
            <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: 'none', marginBottom: 4 }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={2} mb={3}>
                <Typography variant='h5' fontWeight="semibold" color="secondary">{!regionId ? 'Add' : 'Update'} Region</Typography>
                <Box display="flex" alignItems="center" gap={1.25}>
                  <Typography variant='h5' fontWeight="semibold" color="secondary">Status</Typography>
                  <Stack direction="row" alignItems="center" gap={.75} lineHeight={1} p={.75} borderRadius={2} bgcolor="#F2F3F7">
                    <MDButton
                      sx={{ minWidth: '92px', borderRadius: 1.75, minHeight: '28px', p: .25, fontWeight: 500 }}
                      type='button'
                      variant={details.status ? 'contained' : 'text'}
                      color={details.status ? 'success' : 'dark'}
                      size="small"
                      onClick={() => handleStatusChange(true)}
                    >
                      Active
                    </MDButton>
                    <MDButton
                      sx={{ minWidth: '92px', borderRadius: 1.75, minHeight: '28px', p: .25, fontWeight: 500 }}
                      type='button'
                      variant={!details.status ? 'contained' : 'text'}
                      color={!details.status ? 'error' : 'dark'}
                      size="small"
                      onClick={() => handleStatusChange(false)}
                    >
                      Inactive
                    </MDButton>
                  </Stack>
                </Box>
              </Stack>

              <Grid container alignItems="center" spacing={2.5} mb={1}>
                <Grid item xs={12} sm={4}>
                  <TextField
                    fullWidth
                    label="Region Name"
                    error={Boolean(errors.region)}
                    helperText={errors.region}
                    value={details.region || ""}
                    onChange={e => {
                      const { value } = e.target;
                      setDetails(prev => ({ ...prev, region: value }))
                    }}
                  />
                </Grid>
                {/* <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <MobileTimePicker
                      className="mobile-time-picker"
                      label="Opening Hours"
                      value={details.openTime}
                      onChange={time => {
                        setDetails(prev => ({ ...prev, openTime: time }));
                      }}
                      renderInput={(params) => <TextField
                        {...params}
                        error={Boolean(errors.openTime)}
                        helperText={errors.openTime}
                      />}
                    />
                    {Boolean(errors.openTime) && <FormHelperText error>{errors.openTime}</FormHelperText>}
                  </FormControl>
                </Grid> */}
                {/* <Grid item xs={12} sm={4}>
                  <FormControl fullWidth>
                    <MobileTimePicker
                      className="mobile-time-picker"
                      label="Closing Hours"
                      value={details.closeTime}
                      onChange={time => {
                        setDetails(prev => ({ ...prev, closeTime: time }));
                      }}
                      renderInput={(params) =>
                        <TextField {...params}
                          error={Boolean(errors.closeTime)}
                          helperText={errors.closeTime}
                        />}
                    />
                    {Boolean(errors.closeTime) && <FormHelperText error>{errors.closeTime}</FormHelperText>}
                  </FormControl>
                </Grid> */}
              </Grid>
            </Card>
            <Card sx={{ p: { xs: 2, md: 3 }, borderRadius: 2.5, boxShadow: 'none' }}>
              <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ sm: 'center' }} justifyContent="space-between" gap={2}>
                <Typography variant='h5' fontWeight="semibold" color="secondary">{!regionId ? 'Add' : 'Update'} Sub Region</Typography>
              </Stack>
              <Grid item xs={12}>
                {
                  details?.subRegions?.map((subRegion, index) => {
                    const selectedSubRegions = details?.subRegions?.map(s => s.subRegionId);

                    return (
                      <Grid key={index} container spacing={3} className="mt-0 pr-5 position-relative">
                        <Grid item xs={12} md={4}>
                          <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Sub Region</Typography>
                          <ReactSelect
                            className='cus-react-select white-radius-field'
                            classNamePrefix="react-select"
                            name="subRegionId"
                            value={subRegions?.filter(s => s.blazeRegionId === subRegion?.subRegionId)?.map(s => ({ value: s.blazeRegionId, label: s.name }))[0] || null}
                            onChange={option => handleSubRegionChange({ target: { name: "subRegionId", value: option ? option.value : "" } }, index)}
                            options={
                              subRegions
                                ?.filter(s => s.blazeRegionId === subRegion?.subRegionId || !selectedSubRegions.includes(s.blazeRegionId))
                                ?.map(s => ({ value: s.blazeRegionId, label: s.name }))
                            }
                            placeholder="Select Sub Region"
                            components={{ IndicatorSeparator: null }}
                          />
                          {errors?.subRegions?.[index]?.subRegionId && <p className="error">{errors?.subRegions?.[index]?.subRegionId}</p>}
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Typography variant='body2' fontWeight="medium" color={grey[600]} mb={1}>Assigned Driver Today</Typography>
                          <Box
                            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, alignItems: 'center', position: 'relative', justifyContent: "space-between" }}
                            style={{ height: "47px", border: "1px solid #adb5bd", borderRadius: "10rem", padding: "12px 20px", lineHeight: "normal", fontSize: "16px" }}
                            onClick={() => {
                              dispatch(openCustomModalDialog({
                                modalData: {
                                  drivers,
                                  handleDriversList,
                                  onDriverSelect,
                                  index,
                                  selected: subRegion.driverId,
                                  selectedDrivers: details?.subRegions?.map(region => region.driverId)
                                },
                                modal: 'DRIVER_LIST'
                              }))
                            }}
                          >
                            <span>
                              {
                                (() => {
                                  const driver = drivers.find(d => d._id === subRegion.driverId);
                                  if (!driver) return null;
                                  return <>{driver.fleetName}</>
                                })()
                              }
                            </span>
                            <ArrowDown />
                          </Box>
                          {errors?.subRegions?.[index]?.driverId && <p className="error">{errors?.subRegions?.[index]?.driverId}</p>}
                        </Grid>
                        <IconButton
                          className="position-absolute right-0"
                          style={{ top: "60px" }}
                          color='error'
                          size='small'
                          variant='primary'
                          sx={{ marginLeft: 1 }}
                          onClick={() => {
                            const newSubRegions = [...details?.subRegions];
                            newSubRegions.splice(index, 1)
                            setDetails({ ...details, subRegions: newSubRegions });
                          }}
                        >
                          <DeleteIcon width='16px' height='16px' />
                        </IconButton>
                      </Grid>
                    )
                  })
                }
                <Grid container spacing={3} className="mt-0">
                  <Grid item xs={12} md={12}>
                    <MDButton
                      variant="contained"
                      sx={{ minWidth: '160px', background: "#2E2E3A !important", color: "#ffffff !important", fontSize: "14px", minHeight: "36px" }}
                      onClick={() => {
                        setDetails({ ...details, subRegions: [...details?.subRegions, { driverId: "", subRegionId: "" }] });
                      }}
                    >
                      + Add Sub Region
                    </MDButton>
                  </Grid>
                </Grid>
              </Grid>
            </Card>
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1.25} mt={3}>
            <MDButton
              sx={{ minWidth: "160px" }}
              type="button"
              variant="contained"
              color="primary"
              disabled={isLoading}
              onClick={handleSave}
            >
              Save Changes
            </MDButton>
            <MDButton
              sx={{ minWidth: "160px" }}
              type="button"
              variant="outlined"
              color="dark"
              onClick={() => navigate(-1)}
            >
              Cancel
            </MDButton>
          </Stack>
        </Grid>
      </Box >
    </CommonLayout >
  );
}